import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Row} from "reactstrap";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {booleanComparator, dateComparator, formatMoney, numComparator, stringComparator} from "../../../../../../core/services/utils/utils";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, DefaultDataGridPageSizes, dataGridCellTextProps, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {PromotionsListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../components/base/button/icon_button";
import {Add, OpenInNew} from "@mui/icons-material";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import PartnerDataGrid from "../../../../../containers/data-grid";
import PromotionsListSearchSection from "../../../../../components/search-sections/promotions-list-search-section";
import Button from "../../../../../components/base/button/button";
import BarChart from "../../../../../components/base/chart/bar-chart";
import {Paper, useTheme} from "@mui/material";
import Api from "../../../../../../core/services/api/api";
import UpsertPromotionDialog from "../../../../../components/dialogs/upsert-promotion-dialog";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {Link} from "react-router-dom";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../assets/images/validations/failed.svg";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const _initialPaginationInfo = {
    pageSize: 10,
    currentPage: 1,
    length: 0,
    sizes: DefaultDataGridPageSizes,
};

const triggerCurrentPage = -1;

const dataGridSaveConfig = {
    name: 'PromotionsListViewDataGrid',
    version: 3,
}

/**
 * Promotions list action names
 */
const PromotionsListActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const PromotionsListView = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({});
    const [visiblePromotions, setVisiblePromotions] = useState([]);
    const [selectedPromo, setSelectedPromo] = useState(null);
    const [upsertPromotionDialogOpen, setUpsertPromotionDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('promotions-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportPromotionsList = operatorAccessLevels.packages.sales.exportPromotionsList;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.promotionsList);
        configureBehavioralTool((tool) => tool.promotionsList);
    }, [configureBehavioralTool, configureNotebookTags]);


    const isInitialRender = useRef(true);
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridApi = useRef();

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: true,
            },
            {
                name: PromotionsListDataGridCellKeys.actionButton,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: "Code",
                name: PromotionsListDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 160,
            },
            {
                title: "Multiple Use",
                name: PromotionsListDataGridCellKeys.multipleUse,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 110,
            },
            {
                title: "Amount",
                name: PromotionsListDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 90,
            },
            {
                title: "Used",
                name: PromotionsListDataGridCellKeys.used,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 90,
            },
            {
                title: "Remaining",
                name: PromotionsListDataGridCellKeys.remaining,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 90,
            },
            {
                title: "Amount per Redeem",
                name: PromotionsListDataGridCellKeys.giftCards,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 170,
            },
            {
                title: "Apply Amount",
                name: PromotionsListDataGridCellKeys.applyAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 110,
            },
            {
                title: "Created",
                name: PromotionsListDataGridCellKeys.created,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
            },
            {
                title: "Expiry Date",
                name: PromotionsListDataGridCellKeys.expired,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });
    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**@type {function(a, b): number}*/
    const orderByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case PromotionsListDataGridCellKeys.code:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.code ?? '', b?.code ?? '')
                    : stringComparator(b?.code ?? '', a?.code ?? '')
            case PromotionsListDataGridCellKeys.amount:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.amount ?? '', b?.amount ?? '')
                    : numComparator(b?.amount ?? '', a?.amount ?? '')
            case PromotionsListDataGridCellKeys.used:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.amount - a?.remaining ?? '', b?.amount - b?.remaining ?? '')
                    : numComparator(b?.amount - b?.remaining ?? '', a?.amount - a?.remaining ?? '')
            case PromotionsListDataGridCellKeys.remaining:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.remaining ?? '', b?.remaining ?? '')
                    : numComparator(b?.remaining ?? '', a?.remaining ?? '')
            case PromotionsListDataGridCellKeys.giftCards:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.userAmount ?? 0, b?.userAmount ?? 0)
                    : numComparator(b?.userAmount ?? 0, a?.userAmount ?? 0)
            case PromotionsListDataGridCellKeys.created:
                return (a, b) => orderBy.descending
                    ? dateComparator(a?.createDate ?? 0, b?.createDate ?? 0)
                    : dateComparator(b?.createDate ?? 0, a?.createDate ?? 0)
            case PromotionsListDataGridCellKeys.expired:
                return (a, b) => orderBy.descending
                    ? dateComparator(a?.expiryDate ?? 0, b?.expiryDate ?? 0)
                    : dateComparator(b?.expiryDate ?? 0, a?.expiryDate ?? 0)
            case PromotionsListDataGridCellKeys.applyAmount:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.applyAmount ?? 0, b?.applyAmount ?? 0)
                    : numComparator(b?.applyAmount ?? 0, a?.applyAmount ?? 0)
            case PromotionsListDataGridCellKeys.multipleUse:
                return (a, b) => orderBy.descending
                    ? booleanComparator(a?.multipleUse ?? false, b?.multipleUse ?? false)
                    : booleanComparator(b?.multipleUse ?? false, a?.multipleUse ?? false)
            default:
                return () => 0
        }
    }, [orderBy]);

    /**
     * Gets promotions from server by calling the api regarding this action
     * Populates the [data] in the state with result if api call is successful
     * @return {Promise<void>}
     */
    const getPromotions = useCallback(async () => {
        setLoading(true);
        const response = await Api.getActiveCoupons();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            const result = response?.data ?? [];
            setData(result);
        }
    }, [isMounted])

    /**
     * With initial render of the view:
     * Calls the function for getting the list of promotions in the system
     */
    useEffect(() => {
        getPromotions().then();
    }, [getPromotions])

    /**
     * With each change in the filters, resets the pagination information, triggering filtering visible data
     */
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        setPaginationInfo(prevState => ({...prevState, currentPage: triggerCurrentPage}));
    }, [filters])

    /**
     * With each change in the [data] and [paginationInfo] values of the state:
     * - selects a slice of the data to be set as the [visiblePromotions].
     */
    useEffect(() => {
        if (!data?.length) {
            setPaginationInfo(initialPaginationInfo);
            return setVisiblePromotions([]);
        }
        if (paginationInfo.currentPage === triggerCurrentPage) {
            setPaginationInfo(initialPaginationInfo);
            return;
        }
        let results = Array.from(data);
        if (filters?.keyword?.length) {
            results = results?.filter(e =>
                e.code?.toLowerCase().includes(filters.keyword.trim().toLowerCase())
                || e.amount === parseFloat(filters.keyword)
                || (e.amount - e.remaining) === parseFloat(filters.keyword)
                || e.remaining === parseFloat(filters.keyword)
                || e.userAmount === parseFloat(filters.keyword)
            )
        }
        if (filters?.endDate) {
            results = results?.filter(e => DateUtils.server(e.createDate).isSameOrBefore(DateUtils.server(filters?.endDate)))
        }
        if (filters?.startDate) {
            results = results?.filter(e => DateUtils.server(e.createDate).isSameOrAfter(DateUtils.server(filters?.startDate)))
        }
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), results.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, results.length);
        const chunk = Array.from(results).sort(orderByMethod).slice(start, end)
        setPaginationInfo(prevState => ({...prevState, length: results.length}));
        setVisiblePromotions(chunk);
    }, [data, paginationInfo.currentPage, paginationInfo.pageSize, orderByMethod, filters?.keyword, filters?.endDate, filters?.startDate, initialPaginationInfo])

    /**
     * Returns the object for exporting the list of drivers by preparing the title and body of the csv file
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = async () => {
        return {
            title: `Promotions`,
            body: data?.map((e, index) => ({
                '': `${index + 1}`,
                'Code': e.code ?? '--',
                'Amount': formatMoney(e.amount) ?? '--',
                'Used': formatMoney(e.amount - e.remaining) ?? '--',
                'Remaining': formatMoney(e.remaining) ?? '--',
                "Amount per Redeem": e.userAmount ?? '--',
                'Created': DateUtils.serverToLocal(e.createDate).format(DateUtils.dateFormat),
                'Expiry Date': DateUtils.serverToLocal(e.expiryDate).format(DateUtils.dateFormat),
            })) ?? [],
        }
    }

    /**
     * Sorts the entries of the credits in place.
     * @type {(function(*): void)}
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: triggerCurrentPage}));
    }, [])

    /**
     * Handles the result of the insertion of a promotion
     * @type {(function(*, boolean): void)}
     */
    const onUpsertionFinished = useCallback((response, addingNew) => {
        setUpsertPromotionDialogOpen(false);
        dataGridApi.current.resetSelection();
        if (addingNew) {
            setData(prevState => [response, ...prevState])
            return;
        }
        setData(prevState => prevState.map(e => e.id === selectedPromo.id ? response : e))
    }, [selectedPromo])

    /**
     * Opens the dialog for adding a new promotion
     * @type {(function(): void)}
     */
    const addNewPromotion = useCallback(() => {
        setSelectedPromo(null);
        setUpsertPromotionDialogOpen(true);
    }, [])

    /**
     * Opens dialog for editing the selected promotion
     * @type {(function(*): void)}
     */
    const editSelectedPromo = useCallback((promo) => {
        setSelectedPromo(promo);
        setUpsertPromotionDialogOpen(true);
    }, [])

    /**
     * Removes selected promotion from the system by calling the api regarding this action
     * @type {(function(*): Promise<void>)}
     */
    const removePromotion = useCallback(async (promo) => {
        setLoading(true);
        const response = await Api.removeBudget(promo.id);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.id !== promo.id))
        }
    }, [isMounted])

    /**
     * Shows confirmation dialog for removing a promotion
     * @type {(function(*): void)}
     */
    const getRemoveConfirmation = useCallback((promo) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected promotion?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && removePromotion(promo),
        );
    }, [removePromotion])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected) => {
        let _data;
        if (allRowsSelected) {
            // because we have singleSelectionOnly, then we know that pagination.length === 1
            _data = data[0];
        } else {
            _data = selectedRows[0].data;
        }
        switch (action.name) {
            case PromotionsListActionNames.Edit:
                editSelectedPromo(_data);
                break;
            case PromotionsListActionNames.Delete:
                getRemoveConfirmation(_data);
                break;
            default:
                break;
        }
    }

    /**
     * Aggregates data and formats the data values for the barchart
     * @param data
     * @param forUsedAmount
     * @return {{}}
     */
    const getDatasetData = (data, forUsedAmount = false) => {
        const aggregated = {};
        if (forUsedAmount) {
            data?.forEach(record => {
                aggregated[record.code] = (record?.amount ?? 0) - (record?.remaining ?? 0);
            });
        } else {
            data?.forEach(record => {
                aggregated[record.code] = record?.remaining ?? 0;
            });
        }
        return aggregated;
    }

    /**
     * Creates the tool tip value based on the hovered data
     * @type {(function(number[]))}
     */
    const createTooTipValue = useCallback((data) => {
        return `${formatMoney(data)}`
    }, [])

    /**
     * Creates chart data for the bar chart
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartDataPrepared = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        const usedData = getDatasetData(visiblePromotions, true);
        const remainedData = getDatasetData(visiblePromotions, false);
        chartData.datasets.push({
            data: usedData,
            label: 'Used Amount',
            backgroundColor: chartColors[chartColors.length - 1],
        });
        chartData.datasets.push({
            data: remainedData,
            label: 'Remaining Amount',
            backgroundColor: chartColors[0],
        })
        chartData.labels = Object.keys(remainedData) ?? [];
        return chartData;
    }, [chartColors, visiblePromotions])

    /**
     * Memo version of table row records used in data grid
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return visiblePromotions?.map(promo => ({
            key: promo.id,
            data: promo,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [PromotionsListDataGridCellKeys.actionButton]:
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.settings.promotion.single(promo.id)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [PromotionsListDataGridCellKeys.code]: {
                    value: <p {...dataGridCellTextProps} className={'text-sm'}>
                        {promo.code ?? ''}
                    </p>,
                    copyValue: promo.code,
                },
                [PromotionsListDataGridCellKeys.amount]: promo.amount,
                [PromotionsListDataGridCellKeys.used]: promo.amount - promo.remaining,
                [PromotionsListDataGridCellKeys.remaining]: promo.remaining,
                [PromotionsListDataGridCellKeys.giftCards]: promo.userAmount,
                [PromotionsListDataGridCellKeys.created]: promo.createDate,
                [PromotionsListDataGridCellKeys.expired]: promo.expiryDate,
                [PromotionsListDataGridCellKeys.applyAmount]: promo.applyAmount ?? promo?.userAmount,
                [PromotionsListDataGridCellKeys.multipleUse]:
                    promo.multipleUse
                        ? <Passed/>
                        : <Failed/>
            }
        })) ?? []
    }, [visiblePromotions])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: PromotionsListActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: PromotionsListActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
        ], [])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center mt-4 mb-3 pr-2'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-2'}>
                    Promotions
                </h2>
                <Button
                    style={{height: '45px'}}
                    className='d-flex justify-content-center align-items-center ml-3'
                    variant={'outlined'}
                    color={'primary'}
                    onClick={addNewPromotion}
                    disabled={loading}
                >
                    <Add className='icon small'/>
                    <p className='ml-2'>Add Promotion</p>
                </Button>
            </Row>
            <PromotionsListSearchSection filters={filters} setFilters={setFilters}/>
            <div className={'mb-3'}/>
            <Paper className={'paper px-3 py-5 transition mx-auto bar-chart'}>
                <BarChart
                    height={window.innerHeight / 2}
                    displayLegend={false}
                    scales={{
                        scales: {
                            x: {
                                stacked: true,
                            },
                            y: {
                                stacked: true,
                                ticks: {
                                    callback: function (value, index) {
                                        return index === 0 ? '' : `${formatMoney(value)}`
                                    }
                                }
                            },
                        }
                    }}
                    data={chartDataPrepared}
                    createToolTipValue={createTooTipValue}
                />
            </Paper>
            <div className={'mb-5'}/>
            <PartnerDataGrid
                ref={dataGridApi}
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                singleSelectionOnly
                footerActions={footerActions}
                onActionInvoked={onActionInvoked}
                state={{
                    sortBy: orderBy,
                    pagination: paginationInfo,
                    loading: {state: loading},
                    density: dataGridDensity
                }}
                getExportInfo={canExportPromotionsList ? formatExportingData : undefined}
                onSortByChanged={(_, current) => onSort(current)}
                onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                onPageSizeChanged={(_, current) => setPaginationInfo(current)}
            />
            <UpsertPromotionDialog
                open={upsertPromotionDialogOpen}
                setOpen={setUpsertPromotionDialogOpen}
                selectedPromotion={selectedPromo}
                onSuccess={onUpsertionFinished}
            />
        </>
    );
}

export default PromotionsListView;
